import { AnimatePresence, MotionConfig } from "motion/react";
import { QueryClientProvider } from "@tanstack/react-query";
import Home from "@/routes/Home";
import { InitialLoading } from "@/routes/InitialLoading";
import { Onboarding } from "@/routes/Onboarding";
import { AuthProvider, useAuth } from "./providers/auth-provider";
import { UserProvider, useUser } from "./providers/user-provider";
import { SPAProvider } from "./providers/spa-provider";
import { DuelProvider } from "./providers/duel-provider";
import { LoginScreen } from "./components/auth-guard/login-screen";
import { ErrorBoundary } from "./components/error-boundary";
import { Button } from "./components/ui/buttons/button";
import { ToastContainer } from "./components/ui/toast/toast";
import { queryClient } from "./services/query-client";
import { ScreenTransition } from "./motion/motion-primitives";
import { useMinLoadingTime } from "./motion/use-min-loading-time";
import { getRootScreenName } from "./utils/get-root-screen-name";

const SPLASH_MIN_HOLD_MS = 1200;

function UserLoadError({ error }: { error: string }) {
  const { reloadProfile } = useUser();

  return (
    <div className="flex flex-col items-center justify-center gap-4 p-6 text-center">
      <h2 className="text-2xl font-black text-white uppercase">
        Couldn't load your profile
      </h2>
      <p className="text-pk-gray-4 text-sm max-w-xs">{error}</p>
      <Button variant="default" onClick={() => reloadProfile()}>
        <span className="px-8 py-1">Retry</span>
      </Button>
    </div>
  );
}

function RootRouter() {
  const { state: userState } = useUser();
  const { state: authState } = useAuth();

  const rawScreen = getRootScreenName(authState, userState);
  // Once the app leaves "loading", hold the splash a little longer so its
  // designed entrance/exit sequence always gets to play in full.
  const canLeaveSplash = useMinLoadingTime(
    rawScreen !== "loading",
    SPLASH_MIN_HOLD_MS,
  );
  const screen =
    rawScreen !== "loading" && !canLeaveSplash ? "loading" : rawScreen;

  const screenClassName = "w-full h-full flex items-center justify-center";

  return (
    <AnimatePresence mode="wait">
      {screen === "login" && (
        <ScreenTransition key="login" className={screenClassName}>
          <LoginScreen />
        </ScreenTransition>
      )}
      {screen === "loading" && (
        <ScreenTransition
          key="loading"
          className={screenClassName}
          variant="splashTransition"
        >
          <InitialLoading isReady={rawScreen !== "loading"} />
        </ScreenTransition>
      )}
      {screen === "onboarding" && (
        <ScreenTransition key="onboarding" className={screenClassName}>
          <Onboarding />
        </ScreenTransition>
      )}
      {screen === "error" && userState.status === "error" && (
        <ScreenTransition key="error" className={screenClassName}>
          <UserLoadError error={userState.error} />
        </ScreenTransition>
      )}
      {screen === "home" && (
        <ScreenTransition key="home" className={screenClassName}>
          <Home />
        </ScreenTransition>
      )}
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProvider>
          <SPAProvider>
            <DuelProvider>
              <MotionConfig reducedMotion="user">
                <div className="min-h-screen w-full bg-pk-night-2 flex items-center justify-center text-white relative">
                  <ErrorBoundary>
                    <RootRouter />
                  </ErrorBoundary>
                  <ToastContainer />
                </div>
              </MotionConfig>
            </DuelProvider>
          </SPAProvider>
        </UserProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
